import type { ReactElement } from 'react'
import { FlatList, View } from 'react-native'
import type {
  CellRendererProps,
  FlatListProps,
  ImageSourcePropType,
  ListRenderItem,
} from 'react-native'
import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'

import InlineNotification from '@ui-kit/components/InlineNotification'
import Label from '@ui-kit/components/formComponents/Label'

import RadioBox from './RadioBox'
import RadioButton from './RadioButton'
import RadioChip from './RadioChip'
import type { Props, RadioOption, RadioOptionValue } from './types'

const HorizontalCellRenderer = (cellProps: CellRendererProps<RadioOption>) => (
  <View {...cellProps} className="flex-1" />
)

const radioSet = tv({
  base: 'mb-5 gap-y-2.5',
  variants: {
    disabled: {
      true: 'opacity-40',
    },
    noMargin: {
      true: 'mb-0',
    },
  },
})

export const RadioSet = (props: Props) => {
  const onOptionPress = async (newValue: RadioOptionValue) => {
    await props.onValueChange?.(newValue)
  }

  const { radioSetType } = props

  const hasError = props.error !== undefined

  const renderFlatListItem: ListRenderItem<RadioOption> = ({ item: radioOption, index }) => {
    const image =
      typeof radioOption.image === 'string'
        ? { uri: radioOption.image }
        : (radioOption.image as ImageSourcePropType)
    const radioOptionProps = {
      ...radioOption,
      image,
      onPress: onOptionPress,
      checked: radioOption.value === props.value,
      disabled: props.disabled || false,
      isLast: props.radioOptions.length - 1 === index,
      isRow: props.isRow || false,
      layout: props.layout,
      testID: `${props.id}_${radioOption.value}`,
      hasError,
    }
    if (radioOptionProps.hidden) {
      return null
    }
    return match(radioSetType)
      .returnType<ReactElement>()
      .with('radioBox', () => <RadioBox {...radioOptionProps} />)
      .with('radioButton', () => <RadioButton {...radioOptionProps} />)
      .with('radioChip', () => <RadioChip {...radioOptionProps} />)
      .exhaustive()
  }

  const listKeyExtractor = (radioOption: RadioOption) => radioOption.value.toString()

  // FlatLists do not need to virtualize content for tests
  const initialNumToRender = process.env.NODE_ENV === 'test' ? props.radioOptions.length : undefined

  const layoutProps = match({ radioSetType, layout: props.layout })
    .returnType<Partial<FlatListProps<RadioOption>>>()
    .with({ radioSetType: 'radioChip' }, () => ({
      horizontal: true,
      showsHorizontalScrollIndicator: false,
    }))
    .with({ radioSetType: 'radioButton', layout: 'horizontal' }, () => ({
      horizontal: true,
      showsHorizontalScrollIndicator: false,
      contentContainerStyle: { gap: 10 },
    }))
    .with({ layout: 'horizontal' }, () => ({
      horizontal: true,
      showsHorizontalScrollIndicator: false,
      contentContainerStyle: { flexGrow: 1, gap: 10 },
      CellRendererComponent: HorizontalCellRenderer,
    }))
    .otherwise(() => ({}))

  return (
    <View
      testID={props.id}
      className={radioSet({
        noMargin: props.noMargin,
        disabled: props.disabled,
      })}
    >
      {props.label !== undefined ? <Label hasError={hasError}>{props.label}</Label> : null}
      <FlatList
        initialNumToRender={initialNumToRender}
        keyExtractor={listKeyExtractor}
        data={props.radioOptions}
        renderItem={renderFlatListItem}
        {...layoutProps}
      />
      {hasError && props.error !== undefined ? (
        <View>
          <InlineNotification variant="error" message={props.error} />
        </View>
      ) : null}
    </View>
  )
}
